// The three things a shift is (ADR-0029: a shift is drawer custody), each a short Stepper in a centred dialog:
//
//   Start shift — count the float     count the cash into the drawer (by notes and coins, or one total), confirm,
//                                     and the drawer is yours (F-OPS-01).
//   Hand over — count the drawer      the person taking over counts the drawer; the outgoing shift ends on that count,
//                                     the next shift starts with the same cash — theirs (F-OPS-05).
//   Count & close                     the final count by notes and coins (₹2,000 to ₹1) or one total, the card and UPI
//                                     totals and a note, then a summary to confirm (F-OPS-03). Blind by default (the
//                                     expected amount hidden until it is in); where `shifts.blindClose` is off, the
//                                     difference shows live in its tone and a note is asked for past the threshold.
//
// The server owns the money: the float and the variance post through the ledger, the expected drawer is read from it,
// and a hand-over moves the counted cash drawer to drawer (spec/06 §2.4). This counts and sends.

import type { Money, Shift } from '@clavian/api-client';
import { minorUnitDigits } from '@clavian/i18n';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Checkbox, Impact, SegmentedControl, Stepper, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../../api/problems';
import { useRefusalText } from './refusal';
import { Icon } from '../../shell/nav-icons';
import { Facts, Notice } from '../../ui';
import { minorToMajor, moneyFrom } from '../money';
import { useCloseShift, useOpenShift, useShift } from './queries';
import { Variance } from './variance';

import './days-and-shifts.css';

// -------------------------------------------------------------------------------------------------
// Counting the drawer
// -------------------------------------------------------------------------------------------------

/** The notes and coins a drawer is counted in, largest first, in minor units. INR's today; others count a total. */
const DENOMINATIONS: Readonly<Record<string, readonly number[]>> = {
  INR: [200_000, 50_000, 20_000, 10_000, 5_000, 2_000, 1_000, 500, 200, 100],
};

export interface CountState {
  readonly mode: 'notes' | 'total';
  /** How many of each denomination (minor units → count). */
  readonly counts: Readonly<Record<number, number>>;
  /** The typed total, in the major unit. */
  readonly total: string;
}

export function emptyCount(currency: string): CountState {
  return { mode: DENOMINATIONS[currency] === undefined ? 'total' : 'notes', counts: {}, total: '' };
}

/** The counted amount, or null when nothing valid was entered. Integer arithmetic only (spec/06 §1). */
export function countedMoney(state: CountState, currency: string): Money | null {
  if (state.mode === 'total') return moneyFrom(state.total, currency);
  const denominations = DENOMINATIONS[currency] ?? [];
  const amount = denominations.reduce((sum, face) => sum + face * (state.counts[face] ?? 0), 0);
  const touched = denominations.some((face) => state.counts[face] !== undefined);
  return touched ? { amount, currency } : null;
}

/** Count the drawer: a stepper per note and coin with its running total, or one typed total. */
export function DrawerCount({ currency, state, onChange, totalLabel }: { readonly currency: string; readonly state: CountState; readonly onChange: (next: CountState) => void; readonly totalLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const denominations = DENOMINATIONS[currency];
  const counted = countedMoney(state, currency);
  const face = (amount: number) => format.money({ amount, currency }, minorUnitDigits(currency) > 0 && amount % 100 !== 0 ? 'standard' : 'noFraction');
  const setCount = (denomination: number, value: number) => onChange({ ...state, counts: { ...state.counts, [denomination]: Math.max(0, Math.min(9_999, value)) } });

  return (
    <div className="console-form">
      {denominations !== undefined ? (
        <SegmentedControl<CountState['mode']>
          label={t('shifts.count.mode')}
          value={state.mode}
          onValueChange={(mode) => onChange({ ...state, mode })}
          block
          options={[
            { value: 'notes', label: t('shifts.count.byNotes') },
            { value: 'total', label: t('shifts.count.byTotal') },
          ]}
        />
      ) : null}
      {state.mode === 'notes' && denominations !== undefined ? (
        <ul className="console-denoms">
          {denominations.map((denomination) => {
            const amount = face(denomination);
            const count = state.counts[denomination] ?? 0;
            return (
              <li key={denomination} className="console-denom">
                <span className="console-denom__face">{t('shifts.count.denomination', { amount })}</span>
                <span className="console-denom__stepper">
                  <Button variant="secondary" size="sm" icon={<Icon name="minus" />} label={t('shifts.count.fewer', { amount })} disabled={count === 0} onClick={() => setCount(denomination, count - 1)} />
                  <input
                    className="clav-input console-denom__count"
                    inputMode="numeric"
                    aria-label={t('shifts.count.countOf', { amount })}
                    value={state.counts[denomination] === undefined ? '' : String(count)}
                    placeholder="0"
                    onChange={(event) => {
                      const digits = event.target.value.replace(/\D/g, '');
                      setCount(denomination, digits === '' ? 0 : Number(digits));
                    }}
                  />
                  <Button variant="secondary" size="sm" icon={<Icon name="plus" />} label={t('shifts.count.more', { amount })} onClick={() => setCount(denomination, count + 1)} />
                </span>
                <span className="console-denom__total">{t('shifts.count.lineTotal', { amount: face(denomination * count) })}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <TextField label={totalLabel} required numeric inputMode="decimal" value={state.total} onChange={(event) => onChange({ ...state, total: event.target.value })} />
      )}
      {state.mode === 'notes' ? (
        <div className="console-count-total" aria-live="polite">
          <span className="console-count-total__label">{t('shifts.count.total')}</span>
          <span className="console-count-total__value">{format.money(counted ?? { amount: 0, currency })}</span>
        </div>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Start shift — count the float
// -------------------------------------------------------------------------------------------------

export function StartShiftFlow({
  currency,
  organizationId,
  venueId,
  dayLabel,
  counterRequired = false,
  fixedCounterName,
  counterId,
  initialFloat,
  onStarted,
  onDone,
  closeLabel,
  onClose,
}: {
  readonly currency: string;
  readonly organizationId: string;
  readonly venueId: string;
  /** The business day the shift starts in, as the strip names it. */
  readonly dayLabel: string;
  /** Another drawer is open (ADR-0029): this one needs its counter's name. */
  readonly counterRequired?: boolean;
  /** The counter was already picked ("Start your shift", ADR-0031): no name to type. Null: the unnamed counter. */
  readonly fixedCounterName?: string | null;
  /** The counter of the venue's list the drawer is at (ADR-0032); the server names the shift from it. */
  readonly counterId?: string | undefined;
  /** What the float is pre-filled with: the counter's last closing count, or its default float (ADR-0032). */
  readonly initialFloat?: Money | undefined;
  /** Called once the shift has started, before closing. */
  readonly onStarted?: () => void;
  /** Where to go once started; `onClose` by default. */
  readonly onDone?: () => void;
  /** The first step's leave button, "Cancel" by default. */
  readonly closeLabel?: string | undefined;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const format = useVenueFormatter();
  const { show } = useToast();
  const open = useOpenShift(organizationId, venueId);
  const fixed = fixedCounterName !== undefined;
  const [step, setStep] = useState(0);
  const [count, setCount] = useState<CountState>(() =>
    initialFloat === undefined ? emptyCount(currency) : { mode: 'total', counts: {}, total: minorToMajor(initialFloat.amount, initialFloat.currency) },
  );
  const [counterName, setCounterName] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const float = countedMoney(count, currency);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (float === null || float.amount < 0) {
      setStep(0);
      setError(t('shifts.error.amount'));
      return;
    }
    setError(undefined);
    if (step === 0) {
      setStep(1);
      return;
    }
    if (!fixed && counterRequired && counterName.trim() === '') {
      setError(t('shifts.drawers.counterRequired'));
      return;
    }
    const name = fixed ? (fixedCounterName ?? '') : counterName.trim();
    open.mutate(
      { openingFloat: float, ...(counterId !== undefined ? { counterId } : name === '' ? {} : { counterName: name }) },
      {
        onSuccess: () => {
          onStarted?.();
          show({ tone: 'ok', title: t('shifts.flow.started') });
          (onDone ?? onClose)();
        },
        onError: (err) => setError(refusalText(err, t('shifts.error.open'))),
      },
    );
  }

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'float', label: t('shifts.flow.stepFloat') },
          { id: 'confirm', label: t('shifts.flow.stepConfirm') },
        ]}
      />
      <form className="console-form" aria-label={t('shifts.flow.startTitle')} noValidate onSubmit={submit}>
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        {step === 0 ? (
          <DrawerCount currency={currency} state={count} onChange={setCount} totalLabel={t('shifts.field.openingFloat', { currency })} />
        ) : (
          <>
            <div className="console-custody">
              <strong>{t('shifts.flow.confirmTitle')}</strong>
              <span>{t('shifts.flow.confirmBody', { float: format.money(float ?? { amount: 0, currency }), day: dayLabel })}</span>
            </div>
            {fixed ? null : (
              <TextField
                label={t('shifts.field.counterName')}
                {...(counterRequired ? { required: true, description: t('shifts.drawers.counterRequired') } : { showOptional: true })}
                value={counterName}
                onChange={(event) => setCounterName(event.target.value)}
              />
            )}
          </>
        )}
        <div className="console-form__actions console-form__actions--end">
          {step === 0 ? (
            <Button type="button" variant="ghost" onClick={onClose}>
              {closeLabel ?? t('common.cancel')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => setStep(0)}>
              {t('common.back')}
            </Button>
          )}
          <Button type="submit" variant="primary" busy={open.isPending}>
            {step === 0 ? t('common.next') : t('shifts.flow.submitStart')}
          </Button>
        </div>
      </form>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Count & close / Hand over — count the drawer
// -------------------------------------------------------------------------------------------------

export type CountDrawerMode = 'handOver' | 'end';

/** How a count stands against what was expected: level, over or short — and whether it is past the note threshold. */
export function countStanding(counted: Money | null, expected: Money | null, noteOver: Money): { readonly variance: Money | null; readonly tone: 'ok' | 'warn' | 'danger' | null; readonly needsNote: boolean } {
  if (counted === null || expected === null) return { variance: null, tone: null, needsNote: false };
  const variance = { amount: counted.amount - expected.amount, currency: counted.currency };
  const tone = variance.amount === 0 ? 'ok' : variance.amount > 0 ? 'warn' : 'danger';
  return { variance, tone, needsNote: Math.abs(variance.amount) > noteOver.amount };
}

/**
 * Count & close, or hand over (F-OPS-03, F-OPS-05): two steps in a centred dialog. **Count** the cash by notes and coins
 * (₹2,000 down to ₹1) or as one total, with the card reader's and the UPI app's totals; where the venue does not count
 * blind (`shifts.blindClose` off) the expected cash and the difference show live, in their tone, and a note is asked for
 * once the difference passes the venue's threshold. **Confirm** shows a summary of what will be recorded. Under a blind
 * close (the default) the expected amount stays hidden until the shift has closed; a note the server asks for brings
 * the cashier back to the count with the reason.
 */
export function CountDrawerFlow({
  mode,
  currency,
  shiftId,
  ownShift,
  holderName,
  organizationId,
  venueId,
  blind = true,
  noteOver,
  onClose,
}: {
  readonly mode: CountDrawerMode;
  readonly currency: string;
  /** The shift whose drawer is counted. */
  readonly shiftId: string;
  /** The caller opened this shift: their hand-over starts the next shift in their own name. */
  readonly ownShift: boolean;
  /** Who holds the drawer now, for the take-over's words. */
  readonly holderName: string;
  readonly organizationId: string;
  readonly venueId: string;
  /** `shifts.blindClose`: the expected amount stays hidden until the count is recorded. On unless the venue turned it off. */
  readonly blind?: boolean;
  /** `shifts.varianceNoteAmount`: a difference past it needs a note. */
  readonly noteOver?: Money;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const format = useVenueFormatter();
  const close = useCloseShift(organizationId, venueId);
  // The expected cash, read only where the count is not blind.
  const shift = useShift(organizationId, venueId, shiftId, !blind);
  const [step, setStep] = useState(0);
  const [count, setCount] = useState<CountState>(() => emptyCount(currency));
  const [counterCard, setCounterCard] = useState('');
  const [counterUpi, setCounterUpi] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [closed, setClosed] = useState<Shift | null>(null);
  const takingOver = mode === 'handOver' && !ownShift;
  const cash = countedMoney(count, currency);
  const threshold = noteOver ?? { amount: 10_000, currency };
  const expected = blind ? null : (shift.data?.expectedCash ?? null);
  const standing = countStanding(cash, expected, threshold);

  function optional(value: string): Money | null | 'invalid' {
    if (value.trim() === '') return null;
    const money = moneyFrom(value, currency);
    return money === null ? 'invalid' : money;
  }
  const card = optional(counterCard);
  const upi = optional(counterUpi);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    if (cash === null || cash.amount < 0 || card === 'invalid' || upi === 'invalid') {
      setStep(0);
      setError(t('shifts.error.amount'));
      return;
    }
    if (standing.needsNote && note.trim() === '') {
      setStep(0);
      setError(t('shifts.closeFlow.noteNeeded', { amount: format.money(threshold) }));
      return;
    }
    if (step === 0) {
      setStep(1);
      return;
    }
    close.mutate(
      {
        shiftId,
        body: {
          countedCash: cash,
          ...(card === null ? {} : { countedCounterCard: card }),
          ...(upi === null ? {} : { countedCounterUpi: upi }),
          ...(note.trim() === '' ? {} : { note: note.trim() }),
          ...(mode === 'handOver' ? { handOver: true } : {}),
        },
      },
      {
        onSuccess: (result) => {
          setClosed(result);
          setStep(2);
        },
        onError: (err) => {
          // Past the note threshold under a blind count: back to the count, to add the reason.
          if (/note is required/i.test(problemDetail(err) ?? '')) {
            setStep(0);
            setError(t('shifts.closeFlow.noteNeeded', { amount: format.money(threshold) }));
            return;
          }
          setError(refusalText(err, t('shifts.error.close')));
        },
      },
    );
  }

  const title = mode === 'end' ? t('shifts.flow.endTitle') : takingOver ? t('shifts.flow.takeOverTitle') : t('shifts.flow.handOverTitle');
  const submitLabel = mode === 'end' ? t('shifts.flow.submitEnd') : takingOver ? t('shifts.flow.submitTakeOver') : t('shifts.flow.submitHandOver');
  const varianceWord = (value: Money) => (value.amount === 0 ? t('shifts.varianceWord.even') : value.amount > 0 ? t('shifts.varianceWord.over') : t('shifts.varianceWord.short'));

  return (
    <div className="console-stepper-flow console-count-flow">
      <Stepper
        current={step}
        onStepSelect={closed === null ? (index) => (index < step ? setStep(index) : undefined) : undefined}
        steps={[
          { id: 'count', label: t('shifts.closeFlow.stepCount') },
          { id: 'confirm', label: t('shifts.closeFlow.stepConfirm') },
          { id: 'result', label: t('shifts.step.result') },
        ]}
      />
      {closed !== null ? (
        <div className="console-form">
          <span className="console-shift-impact" aria-hidden="true">
            <Impact size={56}>
              <Icon name="check" />
            </Impact>
          </span>
          <Notice tone="ok" role="status" title={mode === 'handOver' ? t('shifts.flow.handedOver', { amount: format.money(cash ?? { amount: 0, currency }) }) : t('shifts.closedTitle')}>
            {t('shifts.closedBody')}
          </Notice>
          <div className="console-shift-result">
            <span className="console-eyebrow">{t('shifts.variance')}</span>
            {closed.cashVariance === null ? <span className="console-subtle">—</span> : <Variance value={closed.cashVariance} />}
          </div>
          <Facts
            columns={2}
            items={[
              { key: 'counted', label: t('shifts.counted'), value: <span className="console-money">{closed.countedCash === null ? '—' : format.money(closed.countedCash)}</span> },
              { key: 'expected', label: t('shifts.expected'), value: <span className="console-money">{format.money(closed.expectedCash)}</span> },
            ]}
          />
          <Button variant="primary" block onClick={onClose}>
            {t('common.done')}
          </Button>
        </div>
      ) : (
        <form className="console-form" aria-label={title} noValidate onSubmit={submit}>
          {error !== undefined ? (
            <Notice tone="danger" role="alert">
              {error}
            </Notice>
          ) : null}
          {step === 0 ? (
            <>
              <p className="console-muted">
                {mode === 'end' ? (blind ? t('shifts.flow.endHint') : t('shifts.closeFlow.endHintOpen')) : takingOver ? t('shifts.flow.takeOverHint', { name: holderName }) : t('shifts.flow.handOverHint')}
              </p>
              {mode === 'handOver' && ownShift ? <Notice tone="info">{t('shifts.flow.handOverOwn')}</Notice> : null}
              {/* Live against the expected cash where the venue does not count blind, kept in view while the notes are counted. */}
              {!blind && expected !== null ? (
                <div className="console-count-live console-rail" data-rail={standing.tone ?? undefined} role="status" aria-live="polite">
                  <div>
                    <span className="console-count-live__label">{t('shifts.expected')}</span>
                    <span className="console-num">{format.money(expected)}</span>
                  </div>
                  <div>
                    <span className="console-count-live__label">{t('shifts.counted')}</span>
                    <span className="console-num">{cash === null ? '—' : format.money(cash)}</span>
                  </div>
                  <div>
                    <span className="console-count-live__label">{t('shifts.closeFlow.difference')}</span>
                    {standing.variance === null ? <span className="console-subtle">—</span> : <Variance value={standing.variance} />}
                  </div>
                </div>
              ) : null}
              <DrawerCount currency={currency} state={count} onChange={setCount} totalLabel={t('shifts.field.countedCash', { currency })} />
              <div className="console-form__row">
                <TextField label={t('shifts.field.counterCard', { currency })} showOptional numeric inputMode="decimal" value={counterCard} onChange={(event) => setCounterCard(event.target.value)} />
                <TextField label={t('shifts.field.counterUpi', { currency })} showOptional numeric inputMode="decimal" value={counterUpi} onChange={(event) => setCounterUpi(event.target.value)} />
              </div>
              <TextField
                label={t('shifts.field.note')}
                required={standing.needsNote}
                showOptional={!standing.needsNote}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                description={standing.needsNote ? t('shifts.closeFlow.noteNeeded', { amount: format.money(threshold) }) : t('shifts.field.noteHint')}
              />
            </>
          ) : (
            <>
              <p className="console-muted">{t('shifts.closeFlow.confirmHint')}</p>
              <Facts
                columns={2}
                items={[
                  { key: 'cash', label: t('shifts.closeFlow.countedCash'), value: <span className="console-money">{format.money(cash ?? { amount: 0, currency })}</span> },
                  ...(standing.variance === null || expected === null
                    ? []
                    : [
                        { key: 'expected', label: t('shifts.expected'), value: <span className="console-money">{format.money(expected)}</span> },
                        { key: 'difference', label: t('shifts.closeFlow.difference'), value: <span>{format.money(standing.variance)} · {varianceWord(standing.variance)}</span> },
                      ]),
                  { key: 'card', label: t('shifts.tender.counter_card'), value: card === null || card === 'invalid' ? t('shifts.closeFlow.notEntered') : <span className="console-money">{format.money(card)}</span> },
                  { key: 'upi', label: t('shifts.tender.counter_upi'), value: upi === null || upi === 'invalid' ? t('shifts.closeFlow.notEntered') : <span className="console-money">{format.money(upi)}</span> },
                  { key: 'note', label: t('shifts.field.note'), value: note.trim() === '' ? t('shifts.closeFlow.noNote') : note.trim() },
                ]}
              />
              {blind ? <p className="console-subtle">{t('shifts.closeFlow.blindAfter')}</p> : null}
              {takingOver ? <Checkbox label={t('shifts.flow.confirmTakeOver')} checked={confirmed} onCheckedChange={(next) => setConfirmed(next === true)} /> : null}
            </>
          )}
          <div className="console-form__actions console-form__actions--end">
            {step === 0 ? (
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
            ) : (
              <Button type="button" variant="ghost" onClick={() => setStep(0)}>
                {t('common.back')}
              </Button>
            )}
            <Button type="submit" variant="primary" busy={close.isPending} disabled={step === 1 && takingOver && !confirmed}>
              {step === 0 ? t('shifts.closeFlow.review') : submitLabel}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
